// shared-lib/cabinet-runs/bayFootprint.js
//
// An appliance bay's size is the APPLIANCE's, read at solve time — never a
// number the bay remembers (#1754). A stored bay carries `width / depth /
// height`, and until now nothing read the object's dimensions back into
// them: a model swap left the reservation stale, and the NEXT re-solve from
// any unrelated trigger re-packed around the old width and re-committed it.
//
// The stored values are kept as the FALLBACK for a bay with no object: the
// appliance-drop ghost (the bay exists a frame before its object), an
// `unplaced` bay, and the scan policy's fill — which passes no objects HERE
// (`ctx.keepStoredBayFootprints`, fillRun.js) because a scan run's bays were
// derived from those very objects moments earlier.
//
// Hosted kinds (sink-base, cooktop) are deliberately NOT derived: their
// reservation is the BASE CABINET the run builds, wider than the bowl
// (`sinkBaseWidthFor`), and a kitchen sink swap leaves `dimensions` alone by
// design (kitchenFixtureSwap.js).
//
// A sink base still has to FIT ITS BOWL, though, which is a weaker claim than
// deriving from it: the bay is widened to `sinkBaseWidthFor(bowl)` — in a
// vanity line's own sizes on a vanity run built in one, `sinkBaseWidthForRun`
// (`vanitySinkBay.js`) — and never narrowed, so a base someone chose wider than it needs is left alone while a
// 32" basin can no longer sit in the 27" base a smaller default sink left
// behind — overhanging its own cabinet by 2½" a side and lapping onto the
// fillers either way along the run (Golden West / Scan 1, 2026-09-08). Pure.

const {
  HOSTED_BAY_KINDS,
  baysForRun,
  isApplianceKind,
  legacyApplianceFromBay,
} = require('./bays');
const { applianceObjectFor } = require('./moveAppliances');
const { sinkBaseWidthForRun } = require('./vanitySinkBay');
const { ovenColumnPartsForBay, columnFootprint } = require('./ovenColumn');

const DIM_EPS = 1e-4; // m — below this two sizes are the same size
const IN_M = 0.0254;

/**
 * The standard openings a dishwasher is built into, inches: a 24" one and a
 * compact 18" one. A dishwasher's BODY is a little narrower than its opening
 * — Fisher & Paykel's 60 cm DD60 is a 599 mm unit for a 600 mm opening, and a
 * US kitchen sets it in a 24" one — and a run that reserved the body packed
 * the cabinets beside it 3/8" tighter than any kitchen is built. The upper over
 * it then fit no cabinet its line sells: a 21" and a bare 2 5/8" strip over a
 * 23 5/8" opening (Cabin, 2428 Lore Rd, 2026-10-26).
 */
const DISHWASHER_OPENINGS_IN = [18, 24];
/** A body this much under an opening, or less, is set in that opening. */
const DISHWASHER_OPENING_SLACK_IN = 1;

/** The opening (metres) a dishwasher `widthM` wide is set in. */
function dishwasherOpeningWidth(widthM) {
  const w = widthM / IN_M;
  const opening = DISHWASHER_OPENINGS_IN.find(
    (o) => w <= o + 1e-6 && o - w <= DISHWASHER_OPENING_SLACK_IN + 1e-6
  );
  return opening != null ? opening * IN_M : widthM;
}

/** Surround types that WRAP an appliance, keyed by what they wrap. */
const SURROUND_FOR = new Map([
  ['fridge-surround', new Set(['refrigerator', 'fridge'])],
  ['oven-surround', new Set(['oven'])],
]);

/** An object's span along its own width axis, in world units. */
function alongOwnAxis(o) {
  const ry = o.rotation?.y || 0;
  const mid = o.center.x * Math.cos(ry) + o.center.z * -Math.sin(ry);
  const half = (o.dimensions?.x || 0) / 2;
  return [mid - half, mid + half];
}

/**
 * The surround built around `appliance`, if one stands on the same wall.
 *
 * The run has to reserve the surround, not the appliance inside it. A fridge
 * surround is its appliance plus a side panel each side — 37" around a 36"
 * fridge on King's Drive F6D83996 — and reserving only the fridge leaves that
 * half inch reading as free run, so the fill packs a 0.5" bay into a panel
 * that is already there and puts a countertop on top of it. The sliver then
 * renders poking out of a wall of full-height cabinetry.
 *
 * Same correction `sinkBaseWidthFor` makes one field over, and for the same
 * reason: the scan measures the appliance, the run has to reserve the
 * cabinetry that actually carries it.
 */
function surroundFor(appliance, surrounds) {
  const wants = appliance?.type;
  for (const s of surrounds || []) {
    if (!SURROUND_FOR.get(s.type)?.has(wants)) continue;
    if (
      !(s.parentWallIds || []).some((w) =>
        (appliance.parentWallIds || []).includes(w)
      )
    )
      continue;
    const [ss, se] = alongOwnAxis(s);
    const [as, ae] = alongOwnAxis(appliance);
    if (as >= ss - 1e-6 && ae <= se + 1e-6) return s;
  }
  return null;
}

/**
 * The SCANNED surround wrapping `appliance`, if any. A surround the fill
 * built (`runId`) is sized FROM the bay — reading it back would grow the
 * reservation by two side panels on every re-solve.
 */
function scannedSurroundFor(appliance, objects) {
  const surrounds = (objects || []).filter(
    (o) => o && !o.runId && SURROUND_FOR.has(o.type) && o.center && o.dimensions
  );
  return surrounds.length ? surroundFor(appliance, surrounds) : null;
}

/**
 * The physical COLUMN a fridge bay stands in: the scanned surround wrapping it,
 * else the appliance itself.
 *
 * What a scribe beside the bay has to match on a path that does NOT rebuild the
 * install (`fridgeColumnSpec` in `fillRun.js`). There the stored bay is no help:
 * the scan policy skips `resolveBayFootprint` altogether, and
 * `scanApplianceAnchors` records an appliance bay's width and depth only — so
 * the height it carries is the generic tier default (70" for a fridge), which
 * on 08E54F9E stood a scribe 2" short of the 72" box beside it.
 */
function scannedColumnFor(bay, objects) {
  // By NAME only. `applianceObjectFor`'s positional fallback is right for a
  // full bay and wrong here: the packer hands `tallNeighbourOf` a slim
  // projection of the bay, and on one without an `applianceId` the matcher
  // answered with a countertop on two corpus rooms and a staircase on a third.
  // No named object means no measured column, which is a fine answer.
  if (!bay?.applianceId) return null;
  const obj = (objects || []).find((o) => o?.id === bay.applianceId);
  if (!obj?.center || !obj?.dimensions) return null;
  return scannedSurroundFor(obj, objects) || obj;
}

const derivable = (bay) =>
  !!bay && isApplianceKind(bay.kind) && !HOSTED_BAY_KINDS.has(bay.kind);

function footprintFromObject(bay, obj, objects) {
  // An oven column is three objects; measuring the one the bay NAMES gives
  // the base band and calls a 109" column 37" tall (proposal §9 step 7).
  // `ovenColumnPartsForBay` is the two-armed resolver — a bay carrying
  // `bay.column` (a fresh deriver-built bay) OR a STALE bay (no `column` key,
  // naming only the base) healed via the named object's own `ovenHousing`
  // flag. Testing `isOvenColumnBay(bay)` alone here was the bug: every dev
  // room's bay is the stale shape until reprocessed, so this branch never
  // fired and the bay kept the base band (18-38") forever.
  const healed = ovenColumnPartsForBay(bay, objects);
  if (healed) {
    const fp = healed.parts.length
      ? columnFootprint({ parts: healed.parts })
      : null;
    if (!fp) return null;
    return { width: fp.width, depth: fp.depth, height: fp.height };
  }
  const d = obj?.dimensions;
  if (!(d?.x > 0)) return null;
  const box =
    bay.kind === 'tallUnit'
      ? d
      : scannedSurroundFor(obj, objects)?.dimensions || d;
  const fp = { width: box.x };
  if (box.z > 0) fp.depth = box.z;
  if (d.y > 0) fp.height = d.y;
  return fp;
}

/**
 * The footprint a floor-standing bay reserves, given the room's objects:
 * width and depth from the scanned surround when one wraps the appliance,
 * height always the appliance's own. `null` when the bay is hosted, has no
 * object, or its object has no width — the caller keeps the stored values.
 */
function resolveBayFootprint(bay, objects) {
  if (!derivable(bay)) return null;
  return footprintFromObject(
    bay,
    applianceObjectFor(bay, objects || []),
    objects
  );
}

const sameDim = (a, b) =>
  (a == null && b == null) ||
  (a != null && b != null && Math.abs(a - b) <= DIM_EPS);

/**
 * Every derivable bay of `run` re-sized from its object. With
 * `flagDangling`, a derivable bay whose object is nowhere in `objects` is
 * marked `dangling: true` (the fill skips it, like `unplaced`; nothing is
 * deleted); a bay whose object is back has the flag cleared. Without it a
 * missing object leaves the bay untouched — the drop path materializes a
 * bay a frame before its object exists. Identity when nothing changes, and
 * when `objects` is not an array.
 */
function resolveRunBayFootprints(run, objects, { flagDangling = false } = {}) {
  if (!run || !Array.isArray(objects)) return run;
  const bays = baysForRun(run);
  let changed = false;
  // A scan MEASURED its sink base. Widening it would repack cabinetry the
  // scan recorded around a bowl the scan also recorded — inventing six inches
  // of casework the room may not have — which is the same trade
  // `fillRun.endPieceBounds` refuses on a scan run. A run that BUILDS its base
  // has no such excuse.
  const fitsBaseToBowl = run.source !== 'scan';
  const next = bays.map((b) => {
    if (b?.kind === 'sink-base') {
      if (!fitsBaseToBowl) return b;
      const basin = applianceObjectFor(b, objects);
      const width = basin?.dimensions?.x
        ? Math.max(b.width || 0, sinkBaseWidthForRun(run, basin.dimensions.x))
        : b.width;
      if (width && !sameDim(width, b.width)) {
        changed = true;
        return { ...b, width };
      }
      return b;
    }
    if (!derivable(b)) return b;
    // A column bay survives while ANY of its sections does: a hand-deleted
    // over-oven cabinet must not retire the reservation the base still needs.
    // `ovenColumnPartsForBay` is the two-armed resolver, so a STALE bay (no
    // `column` key) survives on its remaining sections exactly like a fresh
    // one — testing `isOvenColumnBay(b)` alone here missed that case too.
    const healed = ovenColumnPartsForBay(b, objects);
    const obj = healed
      ? healed.parts[0] || null
      : applianceObjectFor(b, objects);
    if (!obj) {
      // Only a bay that CLAIMS an object can be dangling. A bare reservation
      // — the auto-kitchen's `footprint`-only anchor, which carries no
      // `applianceId` — never named one, so a missing object says nothing
      // about it and it must keep reserving its span. Flagging those made the
      // fill stop reserving a range's column, which stopped the corner plan
      // refusing, which dropped the #1494 corner carry-forward.
      if (!flagDangling || b.dangling || !b.applianceId) return b;
      changed = true;
      return { ...b, dangling: true };
    }
    const measured = footprintFromObject(b, obj, objects);
    // A dishwasher is reserved its OPENING, not its body — on a run that
    // builds its cabinets. A scan's dishwasher bay is what the scan measured,
    // and a reprocess must not move the cabinetry recorded around it.
    const fp =
      measured && b.kind === 'dishwasher' && run.source !== 'scan'
        ? { ...measured, width: dishwasherOpeningWidth(measured.width) }
        : measured;
    const same =
      !b.dangling &&
      (!fp ||
        (sameDim(b.width, fp.width) &&
          sameDim(b.depth, fp.depth) &&
          sameDim(b.height, fp.height)));
    if (same) return b;
    changed = true;
    const { dangling: _gone, ...rest } = b;
    return fp ? { ...rest, ...fp } : rest;
  });
  if (!changed) return run;
  const out = { ...run, bays: next };
  // The legacy view is derived from the bays wherever it is present
  // (rehydrateRun keeps it for the freestanding fill and the drop preview).
  if (Array.isArray(run.appliances)) {
    out.appliances = next
      .filter((b) => isApplianceKind(b.kind))
      .map(legacyApplianceFromBay);
  }
  return out;
}

module.exports = {
  dishwasherOpeningWidth,
  SURROUND_FOR,
  alongOwnAxis,
  surroundFor,
  scannedSurroundFor,
  scannedColumnFor,
  resolveBayFootprint,
  resolveRunBayFootprints,
};
